import React, { createContext, useContext, useEffect, useState, useRef, useMemo, ReactNode } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';

/**
 * User interface for LaunchDarkly identity context.
 * Matches the shape passed from SecurSpaceApp state.
 */
export interface LDUser {
  id: string;
  email?: string;
  companyName?: string;
  [key: string]: any;
}

interface LDIdentityContextValue {
  /** Whether the initial identify call has completed */
  identified: boolean;
  /** The currently identified user, or null if anonymous */
  currentUser: LDUser | null;
}

interface LDIdentityProviderProps {
  children: ReactNode;
  user: LDUser | null;
}

const LDIdentityContext = createContext<LDIdentityContextValue | undefined>(undefined);

/**
 * Hook to access LaunchDarkly identity context.
 * Must be used within an LDIdentityProvider.
 */
export const useLDIdentity = (): LDIdentityContextValue => {
  const context = useContext(LDIdentityContext);
  if (!context) {
    throw new Error('useLDIdentity must be used within an LDIdentityProvider');
  }
  return context;
};

/**
 * LDIdentityProvider - Manages LaunchDarkly user identity lifecycle.
 *
 * Follows the same pattern as PendoProvider and FaroProvider:
 * - Automatically syncs user context with LaunchDarkly when user prop changes
 * - Identifies as anonymous when no user is present
 * - Re-identifies when user logs in or out
 *
 * Note: The LDProvider (asyncWithLDProvider) must still wrap the app in index.js
 * for the SDK to function. This provider only handles identity (the `identify()` call).
 *
 * @example
 * <LDIdentityProvider user={this.state.user}>
 *   <App />
 * </LDIdentityProvider>
 */
export const LDIdentityProvider: React.FC<LDIdentityProviderProps> = ({ children, user }) => {
  const ldClient = useLDClient();
  const [identified, setIdentified] = useState(false);
  const [currentUser, setCurrentUser] = useState<LDUser | null>(null);
  const lastIdentifiedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    const userKey = user?.id ?? null;

    // Skip if we've already identified this exact user key
    if (userKey === lastIdentifiedKeyRef.current) {
      return;
    }

    if (userKey) {
      // User logged in or changed
      const ldContext = {
        kind: 'user' as const,
        key: userKey,
        email: user!.email,
        organization: user!.companyName,
        anonymous: false,
      };

      ldClient.identify(ldContext)
        .then(() => {
          console.debug('[LaunchDarkly] Identified user:', userKey);
          lastIdentifiedKeyRef.current = userKey;
          setIdentified(true);
        })
        .catch(error => console.warn('[LaunchDarkly] Error identifying user:', error));

      setCurrentUser(user);
    } else if (!userKey && lastIdentifiedKeyRef.current !== 'anonymous-user') {
      // User logged out — revert to anonymous
      const anonymousContext = {
        kind: 'user' as const,
        anonymous: true,
        key: 'anonymous-user',
      };

      ldClient.identify(anonymousContext)
        .then(() => {
          console.debug('[LaunchDarkly] Reverted to anonymous context');
          lastIdentifiedKeyRef.current = 'anonymous-user';
          setIdentified(true);
        })
        .catch(error => console.warn('[LaunchDarkly] Error reverting to anonymous:', error));

      setCurrentUser(null);
    } else if (!identified) {
      // Initial mount with no user — mark as identified since bootstrap context is already set
      lastIdentifiedKeyRef.current = 'anonymous-user';
      setIdentified(true);
    }
    // identified is intentionally excluded — ref tracks identity state to prevent extra identify() calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, ldClient]);

  const contextValue = useMemo(() => ({
    identified,
    currentUser,
  }), [identified, currentUser]);

  return (
    <LDIdentityContext.Provider value={contextValue}>
      {children}
    </LDIdentityContext.Provider>
  );
};
